import React, { useState } from 'react';
import { useRecordContext, useDataProvider, Button } from 'react-admin';

const travelTypeOptions = [
    { value: '', label: 'N/A' },
    { value: 'paid', label: 'Paid' },
    { value: 'included', label: 'Included in day' },
];

const PenaltyCalculatorPreview = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();

    const [shootDayTypes, setShootDayTypes] = useState([]);
    const [metaLoaded, setMetaLoaded] = useState(false);

    const [form, setForm] = useState({
        shoot_day_type_id: '',
        call_time: '08:00',
        end_time: '18:00',
        ach: false,
        previous_wrap: '',
        travel_in: '',
        travel_in_type: '',
        travel_out: '',
        travel_out_type: '',
        delayed_meal: '',
        curtailed_meal: '',
    });

    const [results, setResults] = useState(null);
    const [calculating, setCalculating] = useState(false);
    const [error, setError] = useState(null);

    const loadMeta = async () => {
        if (metaLoaded || !record) return;
        try {
            const sdtRes = await dataProvider.getList('shoot-day-types', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {},
            });
            setShootDayTypes(sdtRes.data);
            setMetaLoaded(true);
        } catch (e) {
            setError('Failed to load shoot day types.');
        }
    };

    const handleChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleCalculate = async () => {
        setCalculating(true);
        setError(null);
        setResults(null);

        try {
            const token = localStorage.getItem('auth');
            const pid = record.production_id;
            const apiUrl = (process.env.REACT_APP_API_URL || 'http://timesheetapi.docker') + '/v1';

            const body = {
                penalty_group_id: record.id,
                shoot_day_type_id: parseInt(form.shoot_day_type_id),
                call_time: form.call_time,
                end_time: form.end_time,
                ach: form.ach,
            };

            if (form.previous_wrap) body.previous_wrap = form.previous_wrap;
            if (form.travel_in) {
                body.travel_in = form.travel_in;
                if (form.travel_in_type) body.travel_in_type = form.travel_in_type;
            }
            if (form.travel_out) {
                body.travel_out = form.travel_out;
                if (form.travel_out_type) body.travel_out_type = form.travel_out_type;
            }
            if (form.delayed_meal) body.delayed_meal = parseInt(form.delayed_meal);
            if (form.curtailed_meal) body.curtailed_meal = parseInt(form.curtailed_meal);

            const res = await fetch(`${apiUrl}/productions/${pid}/preview-penalties`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });

            const data = await res.json();

            if (!res.ok) {
                setError(data.message || 'Calculation failed.');
            } else {
                setResults(data);
            }
        } catch (e) {
            setError('An error occurred while calculating.');
        } finally {
            setCalculating(false);
        }
    };

    if (!record) return null;

    const inputStyle = { padding: '6px 8px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px', width: '100%', boxSizing: 'border-box' };
    const labelStyle = { display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' };
    const fieldStyle = { marginBottom: '12px' };

    return (
        <div style={{ marginTop: '24px', padding: '16px', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Test Calculator</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#666' }}>
                Preview penalty calculations for this group without saving to a timesheet.
            </p>
            <hr style={{ margin: '0 0 16px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Shoot Day Type</label>
                    <select style={inputStyle} value={form.shoot_day_type_id} onChange={handleChange('shoot_day_type_id')} onFocus={loadMeta}>
                        <option value="">Select shoot day type...</option>
                        {shootDayTypes.map(s => (
                            <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                        ))}
                    </select>
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Call Time</label>
                    <input style={inputStyle} type="time" value={form.call_time} onChange={handleChange('call_time')} />
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>End Time</label>
                    <input style={inputStyle} type="time" value={form.end_time} onChange={handleChange('end_time')} />
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Previous Wrap (broken turnaround)</label>
                    <input style={inputStyle} type="time" value={form.previous_wrap} onChange={handleChange('previous_wrap')} />
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Travel In</label>
                    <input style={inputStyle} type="time" value={form.travel_in} onChange={handleChange('travel_in')} />
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Travel In Type</label>
                    <select style={inputStyle} value={form.travel_in_type} onChange={handleChange('travel_in_type')} disabled={!form.travel_in}>
                        {travelTypeOptions.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Travel Out</label>
                    <input style={inputStyle} type="time" value={form.travel_out} onChange={handleChange('travel_out')} />
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Travel Out Type</label>
                    <select style={inputStyle} value={form.travel_out_type} onChange={handleChange('travel_out_type')} disabled={!form.travel_out}>
                        {travelTypeOptions.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Delayed Meal (mins)</label>
                    <input style={inputStyle} type="number" value={form.delayed_meal} onChange={handleChange('delayed_meal')} min="0" />
                </div>

                <div style={fieldStyle}>
                    <label style={labelStyle}>Curtailed Meal (mins)</label>
                    <input style={inputStyle} type="number" value={form.curtailed_meal} onChange={handleChange('curtailed_meal')} min="0" />
                </div>

                <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', paddingTop: '20px' }}>
                    <input type="checkbox" id="ach" checked={form.ach} onChange={handleChange('ach')} style={{ marginRight: '8px' }} />
                    <label htmlFor="ach" style={{ fontSize: '14px' }}>ACH</label>
                </div>

            </div>

            <div style={{ marginTop: '8px' }}>
                <Button
                    label={calculating ? 'Calculating...' : 'Calculate'}
                    onClick={handleCalculate}
                    disabled={!form.shoot_day_type_id || calculating}
                    variant="contained"
                />
            </div>

            {error && (
                <div style={{ marginTop: '12px', padding: '8px 12px', background: '#ffebee', color: '#c62828', borderRadius: '4px', fontSize: '13px' }}>
                    {error}
                </div>
            )}

            {results !== null && (
                <div style={{ marginTop: '16px' }}>
                    <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', marginBottom: '12px' }} />
                    <strong style={{ fontSize: '14px' }}>Results</strong>
                    {results.length === 0 ? (
                        <p style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>No penalties calculated for this shift.</p>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                            {results.map((p, i) => (
                                <span key={i} style={{ padding: '4px 10px', border: '1px solid #1976d2', borderRadius: '16px', fontSize: '13px', color: '#1976d2' }}>
                                    {p.penalty_type}: {p.length}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PenaltyCalculatorPreview;